import { supabase } from '../lib/supabase';

/** Public edge function that handles unsubscribe links and the Settings email preference. */
export const EMAIL_UNSUBSCRIBE_FUNCTION = 'email-unsubscribe';

/** Error from the email-unsubscribe function. `status` 404 means the token is unknown. */
export class EmailPreferencesError extends Error {
  /**
   * @param {string} message - Message safe to show.
   * @param {number} status - HTTP status (0 when the request never got a reply).
   */
  constructor(message, status) {
    super(message);
    this.name = 'EmailPreferencesError';
    this.status = status;
  }
}

/**
 * Whether an error means the unsubscribe token is not recognised.
 * @param {unknown} error - Error.
 * @returns {boolean} True for a 404 reply.
 */
export function isUnknownTokenError(error) {
  return error instanceof EmailPreferencesError && error.status === 404;
}

/**
 * Full URL of the email-unsubscribe function.
 * @returns {string} Endpoint URL.
 */
export function emailUnsubscribeEndpoint() {
  const base = (process.env.REACT_APP_SUPABASE_URL || '').replace(/\/+$/, '');
  return `${base}/functions/v1/${EMAIL_UNSUBSCRIBE_FUNCTION}`;
}

/**
 * Current access token, or null.
 * @returns {Promise<string|null>} Token.
 */
async function accessToken() {
  try {
    const result = await supabase?.auth?.getSession?.();
    return result?.data?.session?.access_token || null;
  } catch {
    return null;
  }
}

/**
 * POST JSON to the email-unsubscribe function.
 * @param {object} body - JSON body.
 * @param {Record<string, string>} [extraHeaders] - Extra headers (auth).
 * @returns {Promise<object>} Parsed JSON reply.
 * @throws {EmailPreferencesError} On any non-2xx reply or network failure.
 */
async function post(body, extraHeaders = {}) {
  if (typeof fetch !== 'function') throw new EmailPreferencesError('Network unavailable.', 0);
  let response;
  try {
    response = await fetch(emailUnsubscribeEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...extraHeaders },
      body: JSON.stringify(body),
    });
  } catch {
    throw new EmailPreferencesError('We could not reach Quill AI. Check your connection and try again.', 0);
  }
  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }
  if (!response.ok) {
    const message =
      response.status === 404
        ? 'This link is not valid.'
        : (typeof data?.error === 'string' && data.error) || 'Something went wrong. Please try again.';
    throw new EmailPreferencesError(message, response.status);
  }
  return data || {};
}

/**
 * Read an opt-out flag from a reply, accepting camelCase, snake_case or a status string.
 * @param {object} data - Reply.
 * @returns {boolean} True when the user is opted out of marketing email.
 */
export function readOptOut(data) {
  if (typeof data?.marketingOptOut === 'boolean') return data.marketingOptOut;
  if (typeof data?.marketing_opt_out === 'boolean') return data.marketing_opt_out;
  if (typeof data?.preference?.marketingOptOut === 'boolean') return data.preference.marketingOptOut;
  if (data?.status === 'unsubscribed') return true;
  return false;
}

/**
 * Unsubscribe or resubscribe with the token from an email link (no sign-in needed).
 * @param {string} token - Unsubscribe token from the `t` query parameter.
 * @param {'unsubscribe'|'resubscribe'} action - What to do.
 * @returns {Promise<{status: 'unsubscribed'|'subscribed', email: string|null}>} Result.
 * @throws {EmailPreferencesError} On failure (404 for an unknown token).
 */
export async function updateSubscriptionByToken(token, action) {
  if (!token) throw new EmailPreferencesError('This link is not valid.', 404);
  const data = await post({ token, action });
  const status = data.status === 'subscribed' || data.status === 'unsubscribed'
    ? data.status
    : action === 'unsubscribe' ? 'unsubscribed' : 'subscribed';
  return { status, email: typeof data.email === 'string' ? data.email : null };
}

/**
 * POST an authenticated preference action for the signed-in user.
 * @param {object} body - JSON body.
 * @returns {Promise<object>} Reply.
 */
async function postAuthenticated(body) {
  const token = await accessToken();
  if (!token) throw new EmailPreferencesError('Sign in to change email preferences.', 401);
  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
  return post(body, { Authorization: `Bearer ${token}`, ...(anonKey ? { apikey: anonKey } : {}) });
}

/**
 * The signed-in user's marketing email preference.
 * @returns {Promise<{marketingOptOut: boolean}>} Preference.
 * @throws {EmailPreferencesError} On failure.
 */
export async function getMarketingPreference() {
  const data = await postAuthenticated({ action: 'get_preference' });
  return { marketingOptOut: readOptOut(data) };
}

/**
 * Set the signed-in user's marketing email preference.
 * @param {boolean} marketingOptOut - True to stop product emails.
 * @returns {Promise<{marketingOptOut: boolean}>} Saved preference.
 * @throws {EmailPreferencesError} On failure.
 */
export async function setMarketingPreference(marketingOptOut) {
  const data = await postAuthenticated({ action: 'set_preference', marketingOptOut: Boolean(marketingOptOut) });
  const hasFlag =
    typeof data?.marketingOptOut === 'boolean' ||
    typeof data?.marketing_opt_out === 'boolean' ||
    typeof data?.preference?.marketingOptOut === 'boolean' ||
    data?.status === 'unsubscribed' ||
    data?.status === 'subscribed';
  return { marketingOptOut: hasFlag ? readOptOut(data) : Boolean(marketingOptOut) };
}
